// src/generators/dzial2_algebra/temat3_nierownosci/podtemat1_liniowe_types/LinearInequalityTwoSidesTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";

/* ===== Helpers (spójne z wcześniejszym typem) ===== */
const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; };
function simplifyFrac(num, den) { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { num: num / g, den: den / g }; }
function rawFracLatex(num, den) { if (den < 0) { num = -num; den = -den; } const s = num < 0 ? "-" : ""; const N = Math.abs(num), D = den; return `${s}\\dfrac{${N}}{${D}}`; }
function mixedLatex(num, den) {
  let { num: N, den: D } = simplifyFrac(num, den);
  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);
  if (N % D === 0) return (sign ? `${sign}${N / D}` : `${N / D}`);
  const A = Math.floor(N / D), R = N % D;
  if (A > 0) return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
  return `${sign}\\dfrac{${R}}{${D}}`;
}
const withSign = (n) => (n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`);
const coeffX  = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);

// znaki
const SIGN_KINDS = ["lt","gt","le","ge"]; // <, >, ≤, ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

/* ===== Generator ===== */
/** forceKind: "lt" | "gt" | "le" | "ge" (opcjonalnie) */
export function generateTask(generationOptions) {
  const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // A, C ∈ [-9..-2]∪[2..9], A != C, bez ±1 i 0
  const pickSlope = () => {
    let k = 0;
    while (k === 0 || Math.abs(k) === 1) k = rnd(-9, 9);
    return k;
  };
  let A = pickSlope();
  let C = pickSlope();
  while (C === A) C = pickSlope();

  // B, D ≠ 0
  let B = 0, D = 0;
  while (B === 0) B = rnd(-12, 12);
  while (D === 0) D = rnd(-12, 12);

  const forceKind = typeof generationOptions === "string"
    ? generationOptions
    : generationOptions?.kind;
  const kind = forceKind ?? SIGN_KINDS[rnd(0, SIGN_KINDS.length - 1)];
  const signLatex = SIGN_LATEX[kind];

  const latex = `${coeffX(A)} ${withSign(B)} \\; ${signLatex} \\; ${coeffX(C)} ${withSign(D)}`;

  // Próg po przeniesieniu na jedną stronę:
  // (A−C)x  {sign?}  (D−B)
  const K = A - C;                    // dzielnik
  const N = D - B;                    // licznik
  const t_val = N / K;

  const wantsLess = (kind === "lt" || kind === "le");
  const inclusive = (kind === "le" || kind === "ge");
  const finalLess = K > 0 ? wantsLess : !wantsLess;  // odwrócenie przy K<0

  return {
    type: "LinearInequalityTwoSidesTask",
    data: { A, B, C, D, kind, latex, K, N, t_val, inclusive, finalLess }
  };
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <BlockMath math={data.latex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}><InlineMath math={"x\\;\\in"} /></div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2/3]  lub  [2/3, ∞)"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : (userInput?.S?.raw || userInput?.S || "");
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== Walidacja ===== */
export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : (userInput?.S?.raw || "");
  if (!raw) return { correct: false };

  const parsed = parseIntervalInput(raw);
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1) return { correct: false };
  const I = parsed.intervals[0];

  const t = data.t_val;
  const tol = 1e-6, eq = (x,y) => Math.abs(x - y) <= tol;

  let ok = false;
  if (data.finalLess) {
    // (-∞, t]  lub  (-∞, t)
    ok = I.left === -Infinity &&
         Number.isFinite(I.right) && eq(I.right, t) &&
         (!!I.rightClosed === !!data.inclusive);
  } else {
    // [t, ∞)  lub  (t, ∞)
    ok = I.right === Infinity &&
         Number.isFinite(I.left) && eq(I.left, t) &&
         (!!I.leftClosed === !!data.inclusive);
  }
  return { correct: !!ok };
}

/* ===== Poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const tL = mixedLatex(data.N, data.K);
  if (data.finalLess) {
    const rb = data.inclusive ? "]" : ")";
    return <InlineMath math={`x\\in (-\\infty,\\; ${tL}${rb}`} />;
  } else {
    const lb = data.inclusive ? "[" : "(";
    return <InlineMath math={`x\\in ${lb}${tL},\\; \\infty)`} />;
  }
}

/* ===== Wyjaśnienie ===== */
export function renderExplanation({ data }) {
  const { A, B, C, D, kind, K, N, t_val, inclusive, finalLess } = data;

  const initSign = SIGN_LATEX[kind];
  const finalSign = finalLess ? (inclusive ? "\\le" : "<") : (inclusive ? "\\ge" : ">");

  const barMinusCx = `-\\,${coeffX(C)}`;
  const barMinusB  = B < 0 ? `+\\,${Math.abs(B)}` : `-\\,${B}`;
  const barDiv     = K < 0 ? `:\\,(${K})\\;\\text{(odwracamy znak)}` : `:\\,${K}`;

  const rawT = rawFracLatex(N, K);
  const finalT = mixedLatex(N, K);
  const needNext = (() => {
    const { num: sN, den: sD } = simplifyFrac(N, K);
    return Math.abs(sN) >= sD || gcd(Math.abs(N), Math.abs(K)) > 1;
  })();

  // Zapis w kolumnach: alignedat + ręczne \; dla naturalnych odstępów
  const L1 = String.raw`${coeffX(A)} ${withSign(B)} &\; ${initSign} &\; ${coeffX(C)} ${withSign(D)} & \quad \big|\, ${barMinusCx}`;
  const L2 = String.raw`${coeffX(A - C)} ${withSign(B)} &\; ${initSign} &\; ${D} & \quad \big|\, ${barMinusB}`;
  const L3 = String.raw`${coeffX(A - C)} &\; ${initSign} &\; ${D - B} & \quad \big|\, ${barDiv}`;
  const L4 = String.raw`x &\; ${finalSign} &\; ${rawT} & `;
  const L5 = String.raw`x &\; ${finalSign} &\; ${finalT} & `;

  const mathAligned = needNext
    ? String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3} \\
        ${L4} \\[8pt]
        ${L5}
      \end{alignedat}`
    : String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3} \\
        ${L4}
      \end{alignedat}`;

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Rozwiązujemy nierówność (ciąg przekształceń po kreskach).</p>
      <div style={{ textAlign: "center" }}>
        <BlockMath math={mathAligned} />
      </div>

      <p><strong>Krok 2:</strong> Zaznaczamy rozwiązanie na osi liczbowej.</p>
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineInterval
          t={t_val}
          tLabelLatex={finalT}
          dir={finalLess ? "left" : "right"}
          closed={inclusive}
          margin={2.8}
          showZero={true}   // ← POKAZUJEMY 0 NA OSI (zmiana)
        />
      </div>

      <p><strong>Odpowiedź:</strong></p>
      <div style={{ textAlign: "center" }}>
        {finalLess
          ? <BlockMath math={`x\\in \\; (-\\infty,\\; ${finalT}${inclusive ? "]" : ")"}`} />
          : <BlockMath math={`x\\in \\; ${inclusive ? "[" : "("}${finalT},\\; \\infty)`} />
        }
      </div>
    </div>
  );
}
